import { useCallback } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useProject } from "@/hooks/use-project";
import { ReportsView } from "@/components/reports/reports-view/reports-view";
import type { ReportKindSelection } from "@/components/reports/report-kind-filter/report-kind-filter";

interface ReportsSearch {
  /**
   * The kind the list is narrowed to; absent for All. In the URL, as the
   * Issues page's status is, so a filtered list is linkable, survives a
   * refresh, and is still selected when Back returns from a report. See
   * [[reports-view.spec]] § Filtering by kind.
   */
  kind?: "issues" | "photos";
}

export const Route = createFileRoute("/projects/$projectId/reports/")({
  validateSearch: (search: Record<string, unknown>): ReportsSearch => ({
    kind:
      search.kind === "issues" || search.kind === "photos"
        ? search.kind
        : undefined,
  }),
  component: ReportsPage,
});

export function ReportsPage() {
  const { projectId } = useParams({
    from: "/projects/$projectId/reports/",
  });
  const navigate = useNavigate({ from: "/projects/$projectId/reports/" });
  const search = Route.useSearch();
  const { data: project } = useProject(projectId);

  // Replaces, so switching kinds doesn't leave a trail for Back to walk.
  const setKind = useCallback(
    (kind: ReportKindSelection) => {
      void navigate({
        search: (prev) => ({ ...prev, kind: kind ?? undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  if (!project) return null;

  return (
    <ReportsView
      projectId={projectId}
      project={project}
      kind={search.kind ?? null}
      onKindChange={setKind}
    />
  );
}
